import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

const teamBgUrl = "https://readdy.ai/api/search-image?query=Scholarly%20Chinese%20cultural%20research%20library%20with%20ancient%20books%20wooden%20bookshelves%20warm%20golden%20light%20academic%20atmosphere%20traditional%20aesthetics%20peaceful%20study%20environment%20soft%20natural%20window%20light&width=1600&height=600&seq=about-team-bg&orientation=landscape";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u26av8l97n3eas3fug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          return;
        }
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <main className="pt-20 min-h-screen bg-background-50">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <nav className="flex items-center gap-2 text-xs text-foreground-400" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground-700 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">关于我们</span>
        </nav>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://huayuep.github.io/" },
            { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://huayuep.github.io/about" }
          ]
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于玉蒲团文化研究中心",
          "description": "专注华语经典情色文学与电影改编研究的文化平台",
          "url": "https://huayuep.github.io/about",
          "inLanguage": "zh-Hans"
        }) }} />
      </div>

      {/* Page Title */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 mb-4">
          关于我们
        </h1>
        <p className="text-foreground-500 text-sm md:text-base max-w-3xl leading-relaxed">
          玉蒲团文化研究中心是一个专注于华语经典情色文学与电影改编研究的学术文化平台。
          我们致力于以严谨的学术视角解读经典作品，以开放的文化态度探讨情色艺术的价值与意义。
        </p>
      </section>

      {/* Brand Introduction */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
              品牌介绍
            </h2>
            <div className="space-y-4 text-foreground-600 text-sm leading-relaxed">
              <p>
                玉蒲团文化研究中心成立于2024年，由一群对华语情色文学与电影充满热情的学者
                和文化研究者共同发起。我们的使命是：以学术之光照亮被误解的经典，以文化之眼
                审视被遮蔽的艺术。
              </p>
              <p>
                在中国传统文化中，情色文学长期处于禁忌与边缘化的境地。《金瓶梅》被视为
                "淫书"，《玉蒲团》被贴上"禁毁小说"的标签。然而，当我们以严肃的学术态度重
                新审视这些作品时，会发现它们在叙事结构、人物塑造和文化表达方面都具有不容
                忽视的价值。
              </p>
              <p>
                我们相信，真正的人文研究应当直面人性的全部维度——包括被传统道德所回避的
                欲望与情色。通过对《玉蒲团》等经典作品的深入解读，我们希望帮助读者建立起
                更为立体、更为丰富的中文文学与文化认知。
              </p>
            </div>
          </div>
          <div className="relative h-72 md:h-80 lg:h-full rounded-lg overflow-hidden">
            <img
              src={teamBgUrl}
              alt="玉蒲团文化研究中心 - 学术研究环境"
              className="w-full h-full object-cover object-top rounded-lg"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-focus-3-line",
                title: "学术使命",
                desc: "以严谨的学术研究方法，深入解析华语情色文学与电影的艺术价值和文化意义，推动相关领域的学术讨论。"
              },
              {
                icon: "ri-eye-line",
                title: "文化视野",
                desc: "将情色文学与电影置于广阔的历史文化背景中审视，探讨其与社会变迁、思想解放和艺术发展的互动关系。"
              },
              {
                icon: "ri-heart-line",
                title: "人文关怀",
                desc: "以尊重和理解的态度对待所有文化表达形式，摒弃偏见与禁忌，以开放包容的心态拥抱文化的多元性。"
              },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-8 text-center">
          学术声誉
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { num: "50+", label: "深度研究文章" },
            { num: "12", label: "特邀学者作者" },
            { num: "30万+", label: "累计阅读量" },
            { num: "6", label: "研究方向分类" },
          ].map((stat, i) => (
            <div key={i} className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
              <div className="font-heading font-black text-2xl md:text-3xl text-primary-600 mb-1">
                {stat.num}
              </div>
              <div className="text-foreground-500 text-xs">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 text-center">
            联系我们
          </h2>
          <p className="text-foreground-500 text-sm text-center mb-8">
            欢迎学术交流与合作，我们将尽快回复您的来信
          </p>

          {formStatus === 'success' ? (
            <div className="p-8 rounded-lg bg-background-50 border border-background-200/70 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-check-line text-primary-600 text-2xl"></i>
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
                提交成功！
              </h3>
              <p className="text-foreground-500 text-sm">
                感谢您的来信，我们将在2-3个工作日内回复您。
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/70 space-y-5"
            >
              {/* Honeypot */}
              <div className="spam-field-wrap">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入邮件主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
                <p className="text-foreground-400 text-xs mt-1">最多500字符</p>
              </div>

              {formStatus === 'error' && (
                <div className="p-3 rounded-md bg-accent-100/50 text-accent-800 text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-line mr-2"></i>
                发送留言
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}